import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent, CartAddEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText'];

  /** @type {number} */
  get currentCartCount() {
    // バグ回避
    if(document.querySelector('.cart-bubble__text-count') != null) {
      return parseInt(document.querySelector('.cart-bubble__text-count').textContent ?? '0', 10);
    } else {
      return parseInt(this.refs.cartBubbleText.textContent ?? '0', 10);
    }
  }

  set currentCartCount(value) {
    this.refs.cartBubbleText.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    // 商品が追加できていなければ終了する
    if(event.detail.data.didError) return; 
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);
    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleText.textContent;
    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}

// cart in pop up
document.addEventListener('DOMContentLoaded', ()=>{
  const controller = new AbortController();
  const cart_popup = document.querySelector('.cm-cart-popup');
  const cart_added = document.querySelector('.cm-cart-popup__item');
  document.addEventListener(ThemeEvents.cartUpdate, async (e)=>{
    if(e.detail.data.didError || e.detail.data.source != 'product-form-component') return;
    const cart_icon = document.querySelector('.header-actions__cart-icon');
    const header = document.querySelector('.header__row');
    if(window.innerWidth >= 750) {
      cart_popup.style.right = `${window.innerWidth - cart_icon.getBoundingClientRect().right}px`;
    }
    const cart = await fetch('/cart.js')
      .then(response => response.json())
    const added_product = cart.items.find(item => item.id === Number(e.target.querySelector('input[ref="variantId"]').value));
    cart_added.innerHTML = `<div class="cm-cart-popup__item-image"><img src="${added_product.image}" alt="${added_product.title}"></div><div class="cm-cart-popup__item-info"><h3 class="cm-cart-popup__item-name">${added_product.title}</h3><span class="cm-cart-popup__item-variant">${added_product.variant_title??""}</span></div>`;
    cart_popup.classList.add('is-open');
    cart_popup.setAttribute('aria-hidden', false);
  });

  document.addEventListener('click', (e)=>{
    if(!cart_popup.contains(e.target)) {
      cart_popup.classList.remove('is-open');
      cart_popup.setAttribute('aria-hidden', true);
    }
  });
  
  const popup_close = document.querySelectorAll('.cart-popup-close');
  popup_close.forEach((elm)=>{
    elm.addEventListener('click', ()=>{
      cart_popup.classList.remove('is-open');
      cart_popup.setAttribute('aria-hidden', true);
    });
  });
});
// cart in pop up
